import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import chatService, { ChatConversation, ChatMessage } from '@/services/chatService';
import toast from 'react-hot-toast';
import { ChatRole } from '@hr-outsourcing/shared';
import i18n from '@/lib/i18n';
import { EntityId } from '@/types/common';
interface ChatState {
  conversations: ChatConversation[];
  currentConversation: ChatConversation | null;
  messages: ChatMessage[];
  suggestions: string[];
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
  /** Timestamp (ms) until which the user is rate-limited. null = not limited. */
  rateLimitedUntil: number | null;
}
interface ChatActions {
  fetchConversations: () => Promise<void>;
  fetchConversation: (id: EntityId) => Promise<void>;
  createConversation: (title?: string) => Promise<ChatConversation | null>;
  deleteConversation: (id: EntityId) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  fetchSuggestions: () => Promise<void>;
  setCurrentConversation: (conversation: ChatConversation | null) => void;
  addMessage: (message: ChatMessage) => void;
  setRateLimitedUntil: (until: number | null) => void;
  clearMessages: () => void;
  reset: () => void;
  /** Patch a specific tool call's uiComponent.props within a message.
   * cardKey is the toolCallId (streaming) or the index string (persisted messages). */
  updateCardInMessage: (messageId: EntityId, cardKey: string, patch: Record<string, unknown>) => void;
}
type ChatStore = ChatState & ChatActions;
export const useChatStore = create<ChatStore>()(
  devtools(
    (set, get) => ({
      conversations: [],
      currentConversation: null,
      messages: [],
      suggestions: [],
      isLoading: false,
      isSending: false,
      error: null,
      rateLimitedUntil: null,
      fetchConversations: async () => {
        set({ isLoading: true, error: null });
        try {
          const conversations = await chatService.getConversations();
          set({ conversations, isLoading: false });
        } catch (error) {
          console.error('Failed to fetch conversations:', error);
          set({ isLoading: false, error: i18n.t('chatbot.errors.fetchConversationsFailed') });
        }
      },
      fetchConversation: async (id: EntityId) => {
        set({ isLoading: true, error: null });
        try {
          const { conversation, messages } = await chatService.getConversation(id);
          set({
            currentConversation: conversation,
            messages,
            isLoading: false,
          });
        } catch (error) {
          console.error('Failed to fetch conversation:', error);
          set({ isLoading: false, error: i18n.t('chatbot.errors.fetchConversationFailed') });
        }
      },
      createConversation: async (title?: string) => {
        set({ isLoading: true, error: null });
        try {
          const conversation = await chatService.createConversation(title);
          set((state) => ({
            conversations: [conversation, ...state.conversations],
            currentConversation: conversation,
            messages: [],
            isLoading: false,
          }));
          return conversation;
        } catch (error) {
          console.error('Failed to create conversation:', error);
          set({ isLoading: false, error: i18n.t('chatbot.errors.createConversationFailed') });
          return null;
        }
      },
      deleteConversation: async (id: EntityId) => {
        try {
          await chatService.deleteConversation(id);
          set((state) => {
            const newConversations = state.conversations.filter((c) => c.id !== id);
            const isCurrentDeleted = state.currentConversation?.id === id;
            return {
              conversations: newConversations,
              currentConversation: isCurrentDeleted ? null : state.currentConversation,
              messages: isCurrentDeleted ? [] : state.messages,
            };
          });
          toast.success(i18n.t('chatbot.success.conversationDeleted'));
        } catch (error) {
          console.error('Failed to delete conversation:', error);
          toast.error(i18n.t('chatbot.errors.deleteConversationFailed'));
        }
      },
      sendMessage: async (content: string) => {
        const { currentConversation } = get();
        if (!currentConversation) {
          const newConv = await get().createConversation();
          if (!newConv) return;
        }
        const conversationId = get().currentConversation?.id;
        if (!conversationId) return;
        const tempUserMessage: ChatMessage = {
          id: String(Date.now()),
          role: ChatRole.USER,
          content,
          toolCalls: null,
          createdAt: new Date().toISOString(),
        };
        set((state) => ({
          messages: [...state.messages, tempUserMessage],
          isSending: true,
          error: null,
        }));
        try {
          const assistantMessage = await chatService.sendMessage(conversationId, content);
          set((state) => ({
            messages: [...state.messages, assistantMessage],
            isSending: false,
          }));
          set((state) => ({
            conversations: state.conversations.map((c) =>
              c.id === conversationId
                ? { ...c, messageCount: c.messageCount + 2, updatedAt: new Date().toISOString() }
                : c
            ),
          }));
        } catch (error) {
          console.error('Failed to send message:', error);
          set((state) => ({
            messages: state.messages.slice(0, -1),
            isSending: false,
            error: i18n.t('chatbot.errors.sendMessageFailed'),
          }));
        }
      },
      fetchSuggestions: async () => {
        try {
          const suggestions = await chatService.getSuggestions();
          set({ suggestions });
        } catch (error) {
          console.error('Failed to fetch suggestions:', error);
        }
      },
      setCurrentConversation: (conversation) => {
        set({ currentConversation: conversation });
      },
      addMessage: (message) => {
        set((state) => ({
          messages: [...state.messages, message],
        }));
      },
      setRateLimitedUntil: (until) => {
        set({ rateLimitedUntil: until });
      },
      clearMessages: () => {
        set({ messages: [], currentConversation: null });
      },
      updateCardInMessage: (messageId, cardKey, patch) => {
        set((state) => ({
          messages: state.messages.map((msg) => {
            if (msg.id !== messageId || !msg.toolCalls) return msg;
            // cardKey may be a numeric index (for persisted messages) or a toolCallId string (streaming)
            const numericIndex = /^\d+$/.test(cardKey) ? parseInt(cardKey, 10) : -1;
            // Build index among tool calls that have uiComponent (matching the render filter)
            let uiIndex = 0;
            return {
              ...msg,
              toolCalls: msg.toolCalls.map((tc) => {
                if (!tc.uiComponent) return tc;
                const currentUiIndex = uiIndex++;
                const isMatch = numericIndex >= 0 ? currentUiIndex === numericIndex : false;
                if (!isMatch) return tc;
                const existingUi = tc.uiComponent.props.__ui as Record<string, unknown> | undefined;
                const updatedProps = { ...tc.uiComponent.props, ...patch };
                // If patch contains a status that is not PENDING, clear action buttons
                const patchStatus = patch.status ?? patch.requestStatus;
                const shouldClearActions = patchStatus !== undefined && patchStatus !== 'PENDING';
                if (shouldClearActions && existingUi) {
                  updatedProps.__ui = { ...existingUi, actions: [] };
                }
                return { ...tc, uiComponent: { ...tc.uiComponent, props: updatedProps } };
              }),
            };
          }),
        }));
      },
      reset: () => {
        set({
          conversations: [],
          currentConversation: null,
          messages: [],
          suggestions: [],
          isLoading: false,
          isSending: false,
          error: null,
          rateLimitedUntil: null,
        });
      },
    }),
    { name: 'ChatStore', enabled: import.meta.env.DEV }
  )
);
